import type { FeedEntry, FeedEvent, FeedGame, FeedPlayer } from '@ottd/api';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Hint } from '@/components/prototype/hint';
import { PlayerAvatar } from '@/components/prototype/player-avatar';
import { serverTypeTheme } from '@/lib/server-type';
import { cn } from '@/lib/utils';
import { formatSignedPoints } from '@/lib/format';
import { agoLabel, exactTime, toBlocks } from '@/pages/home/feed-blocks';

/*
 * The homepage server log (prototype `Homepage.dc.html`). A finished game is a
 * CARD - its header naming the game, goal, winning time and player count, with
 * a row per scored player - and anything else is a single EVENT line. Entries
 * fall into time blocks ("JUST NOW", "EARLIER TODAY", …) so the newest work is
 * read first.
 *
 * Data is seeded from `/api/live/servers` and pushed via Echo.
 */

/*
 * Panel chrome, kept out of the class strings that describe layout. All of it
 * is themed: most roles map onto the ordinary palette tokens, and the four
 * with no semantic equivalent live in the `--feed-*` family.
 */
const RULE = 'var(--feed-rule)';
const CARD_BG = 'var(--feed-card)';
const CARD_BORDER = 'var(--line)';
const MUTED = 'var(--faint)';
const STAT = 'var(--dim)';
const WINNER = 'var(--gold)';
const POINTS = 'var(--green)';
/** Dimmest tier: losing places, event timestamps, the empty and trailing notes. */
const FAINT = 'var(--feed-faint)';
const NAME = 'var(--text)';
const NAME_WINNER = 'var(--strong)';

const AVATAR_SIZE = 19;

/**
 * The winner's time. A time anomaly (recorded times shorter than the in-game
 * calendar allows) shows "⏱ ?" with the reason on hover instead.
 */
function WinningTime({ game }: { game: FeedGame }) {
  const { t } = useTranslation('home');

  if (game.timeAnomaly) {
    const label = t('activity.timeUnavailable');
    return (
      <Hint label={label}>
        <span style={{ color: MUTED }}>
          <span aria-hidden="true">⏱ ?</span>
          <span className="sr-only">{label}</span>
        </span>
      </Hint>
    );
  }

  return game.duration ? <span style={{ color: WINNER }}>⏱ {game.duration}</span> : null;
}

/**
 * A relative timestamp, with the exact moment behind it on hover - "2h ago"
 * answers "is this fresh?" at a glance, the tooltip answers "when exactly?".
 */
function Ago({
  time,
  color,
  className,
}: {
  time: string | null;
  color: string;
  className?: string;
}) {
  return (
    <Hint label={exactTime(time)}>
      <span className={cn('shrink-0 font-mono text-xs', className)} style={{ color }}>
        {agoLabel(time)}
      </span>
    </Hint>
  );
}

function BlockHead({ head }: { head: string }) {
  return (
    <div className="flex items-center gap-2 pt-1.5 pb-2">
      <span className="font-display text-[9px]" style={{ color: MUTED }}>
        {head}
      </span>
      <span className="h-px grow" style={{ background: RULE }} />
    </div>
  );
}

function PlayerRow({ player }: { player: FeedPlayer }) {
  const isWinner = player.place === 1;

  return (
    <div className="hover:bg-feed-hover flex items-center gap-[9px] px-2.5 py-[5px]">
      <span
        className="w-[14px] shrink-0 font-mono text-[11px]"
        style={{ color: isWinner ? WINNER : FAINT }}
      >
        {isWinner ? '★' : `#${player.place}`}
      </span>
      <PlayerAvatar name={player.name} src={player.avatar} size={AVATAR_SIZE} decorative />
      <Link
        to={`/player/${encodeURIComponent(player.name)}`}
        className="hover:text-orange truncate text-sm font-semibold"
        style={{ color: isWinner ? NAME_WINNER : NAME }}
      >
        {player.name}
      </Link>
      <div className="ml-auto flex shrink-0 items-center gap-3">
        {player.stat ? (
          <span className="font-mono text-xs" style={{ color: STAT }}>
            {player.stat}
          </span>
        ) : null}
        <span
          className="w-[34px] text-right font-mono text-sm"
          style={{ color: isWinner ? WINNER : POINTS }}
        >
          {formatSignedPoints(player.points)}
        </span>
      </div>
    </div>
  );
}

function GameCard({ game }: { game: FeedGame }) {
  const { t } = useTranslation('home');
  const accent = serverTypeTheme(game.type).monoColor;

  return (
    <div
      className="border border-l-[3px]"
      style={{ background: CARD_BG, borderColor: CARD_BORDER, borderLeftColor: accent }}
    >
      <div
        className="flex flex-wrap items-center gap-2 border-b px-2.5 py-[7px]"
        style={{ borderColor: RULE }}
      >
        {game.gameId ? (
          <Link
            to={`/game/${game.gameId}`}
            className="text-strong hover:text-orange shrink-0 text-sm font-bold"
          >
            {game.title}
          </Link>
        ) : (
          <span className="text-strong shrink-0 text-sm font-bold">{game.title}</span>
        )}
        {game.goal ? (
          <span className="shrink-0 font-mono text-xs" style={{ color: MUTED }}>
            {t('activity.goal', { goal: game.goal })}
          </span>
        ) : null}
        <div className="ml-auto flex shrink-0 items-center gap-2.5 font-mono text-xs">
          <WinningTime game={game} />
          {/* When it ended, not how many played - the rows below are the count. */}
          <Ago time={game.time} color={MUTED} />
        </div>
      </div>
      <div className="flex flex-col py-[3px]">
        {game.players.map((player) => (
          <PlayerRow key={player.id} player={player} />
        ))}
      </div>
    </div>
  );
}

function EventRow({ event }: { event: FeedEvent }) {
  return (
    <div className="hover:bg-feed-hover flex flex-wrap items-center gap-[9px] py-1 pr-2.5 pl-[3px]">
      <PlayerAvatar name={event.name} src={event.avatar} size={AVATAR_SIZE} decorative />
      <Link
        to={`/player/${encodeURIComponent(event.name)}`}
        className="hover:text-orange shrink-0 text-sm font-semibold"
        style={{ color: NAME }}
      >
        {event.name}
      </Link>
      {event.verb ? (
        <span className="shrink-0 text-sm" style={{ color: MUTED }}>
          {event.verb}
        </span>
      ) : null}
      <span className="text-sm font-semibold" style={{ color: WINNER }}>
        {event.what}
      </span>
      <Ago time={event.time} color={FAINT} className="ml-auto" />
    </div>
  );
}

interface ServerLogProps {
  entries: FeedEntry[];
  /** Overrides the panel's own "nothing happened yet" line. */
  emptyLabel?: string;
}

export function ServerLog({ entries, emptyLabel }: ServerLogProps) {
  const { t } = useTranslation('home');
  const blocks = useMemo(() => toBlocks(entries), [entries]);

  return (
    // Fills the panel rather than capping its own height, so the row's two
    // panels end level however tall the one beside it is. `min-h-0` is what
    // lets a flex child actually scroll instead of stretching its parent.
    <div
      className="bg-feed-bg scrollbar-discord min-h-0 flex-1 overflow-y-auto px-4 pt-3 pb-3.5"
      role="log"
      aria-live="polite"
      aria-label={t('activity.title')}
    >
      {blocks.length === 0 ? (
        <p className="text-feed-faint font-mono text-xs">{emptyLabel ?? t('activity.empty')}</p>
      ) : (
        blocks.map((block) => (
          <div key={block.head} className="mb-1.5">
            <BlockHead head={block.head} />
            <div className="flex flex-col gap-[7px]">
              {block.entries.map((entry: FeedEntry) =>
                entry.kind === 'game' ? (
                  <GameCard key={`game-${entry.id}`} game={entry} />
                ) : (
                  <EventRow key={`event-${entry.id}`} event={entry} />
                ),
              )}
            </div>
          </div>
        ))
      )}
      {/* The window ends here, the history doesn't - but only say so when
          there is something for it to trail off from. */}
      {blocks.length > 0 ? (
        <p className="text-feed-faint pt-2 text-center font-mono text-xs">{t('activity.more')}</p>
      ) : null}
    </div>
  );
}
